/**
 * Canonical production site configuration.
 *
 * SITE_URL is the single source of truth for the production origin (no trailing slash).
 * NOTE: index.html embeds the same origin statically for pre-hydration/crawler fallback —
 * keep those values in sync with this constant when the domain changes.
 */
export const SITE_URL = 'https://karamkhoury.me' as const;

/** Absolute URL to the default Open Graph / Twitter share image (1200×630). */
export const OG_IMAGE_URL = `${SITE_URL}/og-image.jpg` as const;

/** First year of Karam's professional software engineering career. */
const CAREER_START_YEAR = 2010;

/** Career-start month (0-indexed: 10 = November), when Cloud Innovations FZCO began. */
const CAREER_START_MONTH = 10;

/**
 * Years of professional experience, computed month-aware from the Nov 2010 career start —
 * a naive `currentYear - CAREER_START_YEAR` would round up to the next year as early as
 * January. Ticks over on 1 November each year. Accepts an optional `now` for testing.
 */
export const getYearsOfExperience = (now: Date = new Date()): number => {
  const years = now.getFullYear() - CAREER_START_YEAR;
  return now.getMonth() < CAREER_START_MONTH ? years - 1 : years;
};

/**
 * Formats the years-of-experience figure the way it should read: the bare number during
 * the Nov anniversary month (the floor is exact — zero months' remainder that month), and
 * "N+" every other month (the floor undercounts by 1-11 months' progress toward the next
 * year). Month-level granularity only — no exact start day is on record.
 * NOTE: index.html and todo.md embed this as a static string for pre-hydration/crawler
 * fallback and blog bylines — when resyncing those by hand each November, don't assume
 * "+" is still correct; check whether the current month is the anniversary month.
 */
export const getYearsOfExperienceLabel = (now: Date = new Date()): string => {
  const years = getYearsOfExperience(now);
  return now.getMonth() === CAREER_START_MONTH ? `${years}` : `${years}+`;
};
